/**
 * Firebase initialization — single source of truth.
 *
 * All data (views, comments, favorites, watchlist, history) now lives in
 * Firestore. Realtime Database was removed to eliminate the deprecated
 * `firebaseio.com` API warning in Lighthouse and to unify the backend.
 *
 * View counters use a distributed-counter pattern (shards) so high write
 * rates never hit the Firestore 1-write-per-document limit.
 */
import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import { getAuth, type Auth } from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";

export const firebaseConfig: Record<string, string> = {
  apiKey: "AIzaSyA7JFb_FRVHACUo3EN9qTqoPzmoMtRPGUw",
  authDomain: "rawanui.firebaseapp.com",
  projectId: "rawanui",
  storageBucket: "rawanui.firebasestorage.app",
  messagingSenderId: "407251913628",
  appId: "1:407251913628:web:86ed81a5704b7ad9205659",
  measurementId: "G-TKSKF4SVEM",
};

/** Admin emails — anyone matching gets access to /dashboard admin panels. */
export const ADMIN_EMAILS = ["jamal@elhizazi.me", "rawan@elhizazi.me"] as const;

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;
let _db: Firestore | null = null;

function ensureApp(): FirebaseApp {
  if (_app) return _app;
  _app = getApps()[0] ?? initializeApp(firebaseConfig);
  return _app;
}

/**
 * Public accessor for the singleton FirebaseApp.
 * All modules (messaging, analytics, etc.) MUST call this instead of
 * re-initializing the app to avoid `[DEFAULT]` duplication warnings and
 * to keep a single source of configuration.
 */
export function ensureFirebaseApp(): FirebaseApp {
  return ensureApp();
}

/** Browser-only: returns the singleton auth instance, or null during SSR. */
export function getFirebaseAuth(): Auth | null {
  if (typeof window === "undefined") return null;
  if (_auth) return _auth;
  _auth = getAuth(ensureApp());
  return _auth;
}

export function getDb(): Firestore | null {
  if (typeof window === "undefined") return null;
  if (_db) return _db;
  _db = getFirestore(ensureApp());
  return _db;
}

/** True when a Firebase config is wired (always true now — config is hard-coded). */
export const FIREBASE_READY = true;
